import { RefObject, useEffect, useRef } from "react";

import {
  citationFromClickTarget,
  openCitationInDocumentPanel,
  type CitationDeepLink,
} from "@/services/citationDeepLink";

/**
 * Intercepts citation links clicked inside a same-origin iframe, so they open
 * in the document panel instead of navigating (#11164).
 *
 * This is the case that actually matters for chat artifacts: the backend
 * converts every docx and pptx artifact to PDF before serving it for preview,
 * so a "docx" the user opens is really rendered by pdf.js inside an iframe.
 * A listener on the React tree never sees those clicks — the event stays
 * inside the frame's own document — so interception has to attach there.
 *
 * The listener runs in the capture phase so it settles the click before the
 * viewer's own link handling can act on it. Nothing here reaches into the
 * frame's content beyond reading the href of the anchor that was clicked; the
 * link itself is left exactly as the document ships it, because the file has
 * to keep working when it is opened outside the app.
 */
const useCitationLinkInterception = (
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  onCitation?: (citation: CitationDeepLink) => void
): void => {
  // Kept in a ref so a caller passing an inline arrow doesn't re-attach the
  // listener on every render.
  const handlerRef = useRef(onCitation);
  handlerRef.current = onCitation;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Same-origin (the viewer is served from our own /public), so the frame's
    // document is reachable; a cross-origin frame would simply yield null.
    const frameDocument = iframeRef.current?.contentDocument;
    if (!frameDocument) {
      return;
    }

    const handleClick = (event: MouseEvent): void => {
      const citation = citationFromClickTarget(event.target);
      if (!citation) {
        return;
      }
      event.preventDefault();
      // A surface that needs the panel opened differently supplies a handler
      // (named agents draw their own split-pane viewer, so they add
      // `inlineViewerOnly`); everything else opens the document panel plainly.
      // Nobody resolves the citation here — that is the backend's answer for
      // every surface.
      const handler = handlerRef.current;
      if (handler) {
        handler(citation);
        return;
      }
      openCitationInDocumentPanel(citation);
    };

    frameDocument.addEventListener("click", handleClick, true);
    return () => {
      frameDocument.removeEventListener("click", handleClick, true);
    };
  }, [iframeRef, enabled]);
};

export default useCitationLinkInterception;
